import React, { useState, useEffect, useCallback } from "react";
import { supabase, login, logout } from "./supabase";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { getDraftThoughts, getThoughtVocabulary, createThought, updateThought, deleteThoughtById } from './services/thoughtService';
import { syncThoughtTagCollections } from './services/collectionService';
import useWindowResize from './hooks/useWindowResize';

// Components
import Header from "./components/Header";
import AppContent from "./pages/AppContent";
import TimelineView from "./pages/TimelineView";
import ShuffleView from "./pages/ShuffleView";
import StarField from "./pages/StarField";
import ReflectView from "./pages/ReflectView";
import DeckView from "./pages/DeckView";
import SearchView from "./pages/SearchView";

// Styles and Utils
import { styles, colors } from "./styles/styles";
import { extractUniqueValues } from "./utils/dataProcessing";
import { ReviewModeProvider, useReviewMode } from "./contexts/ReviewModeContext";

// Hides the top app header when the user is inside the immersive
// drafts-review surface so the card stack gets full vertical space.
const AppHeader = ({ user, sidebarCollapsed }) => {
  const { reviewing } = useReviewMode();
  if (reviewing) return null;
  return (
    <Header
      user={user}
      onLogin={login}
      onLogout={logout}
      sidebarCollapsed={sidebarCollapsed}
    />
  );
};

/**
 * Main App Component
 *
 * Orchestrates the application flow and manages global state
 */
function App() {
  // User and tab state
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("inbox");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Data state
  const [thoughts, setThoughts] = useState([]);
  const [themes, setThemes] = useState([]);
  const [motifs, setMotifs] = useState([]);
  const [tags, setTags] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const isMobile = useWindowResize();

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile]);

  // Fetch the home-page data in two cheap queries instead of one unbounded
  // all-thoughts load:
  //   - getDraftThoughts: only untriaged thoughts (the inbox renders these).
  //     A ~5x smaller row set for a typical backlog, full columns for the cards.
  //   - getThoughtVocabulary: tags/theme/motif across ALL rows (no bodies), so
  //     autocomplete still reflects the user's entire vocabulary.
  // The inbox itself is still derived via thoughts.filter(isDraftThought) in
  // AppContent, which stays the source of truth for what renders.
  const fetchThoughts = useCallback(async (uid) => {
    if (!uid) return;

    setIsDataLoading(true);
    // allSettled, not all: the two queries are independent. If vocabulary
    // fails we still want the inbox to render (and vice versa) — Promise.all
    // would discard a successful result just because its sibling rejected.
    const [draftsResult, vocabResult] = await Promise.allSettled([
      getDraftThoughts(uid),
      getThoughtVocabulary(uid),
    ]);

    if (draftsResult.status === 'fulfilled') {
      setThoughts(draftsResult.value);
    } else {
      console.error("Error fetching drafts:", draftsResult.reason);
    }

    if (vocabResult.status === 'fulfilled') {
      const uniqueValues = extractUniqueValues(vocabResult.value);
      setThemes(uniqueValues.themes);
      setMotifs(uniqueValues.motifs);
      setTags(uniqueValues.tags);
    } else {
      console.error("Error fetching vocabulary:", vocabResult.reason);
    }

    setIsDataLoading(false);
  }, []);

  // Listen for authentication state changes (Supabase)
  useEffect(() => {
    let mounted = true;
    let currentUserId = null;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setUser(session?.user || null);
      if (session?.user) {
        currentUserId = session.user.id;
        fetchThoughts(session.user.id);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return;

      // Explicit sign-out is the ONLY event that should tear down the authed UI.
      if (event === 'SIGNED_OUT') {
        currentUserId = null;
        setUser(null);
        return;
      }

      // Ignore transient null-session events (e.g. a token-refresh blip on tab
      // refocus). Flipping `user` to null here would unmount the whole
      // `{user && <Routes>}` subtree and remount it on the next event — which
      // resets in-page state like the deck's shuffled position.
      const nextUser = session?.user;
      if (!nextUser) return;

      // Only update state when the user actually changes identity. Redundant
      // SIGNED_IN/TOKEN_REFRESHED events for the same user must not re-set
      // `user` (a new object reference re-renders; a falsy→truthy flip remounts).
      if (nextUser.id !== currentUserId) {
        currentUserId = nextUser.id;
        setUser(nextUser);
        fetchThoughts(nextUser.id);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [fetchThoughts]);

  // Save a thought to Supabase
  const saveThought = async (thoughtData, skipReload = false) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const uid = session.user.id;

    try {
      let savedThoughtId = thoughtData.id;

      if (thoughtData.id) {
        await updateThought(uid, thoughtData.id, thoughtData);
      } else {
        savedThoughtId = await createThought(uid, thoughtData);
      }

      // Sync tag collections (adds new tag links, removes stale ones)
      await syncThoughtTagCollections({
        ...thoughtData,
        id: savedThoughtId
      });

      if (!skipReload) {
        fetchThoughts(uid);
      }
    } catch (error) {
      console.error("Error saving thought:", error);
    }
  };

  // Update a thought locally without triggering a reload
  const updateThoughtLocally = (updatedThought) => {
    setThoughts(prevThoughts =>
      prevThoughts.map(thought =>
        thought.id === updatedThought.id ? updatedThought : thought
      )
    );
  };

  // Delete a thought from Supabase
  const deleteThought = async (thoughtId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user || !thoughtId) return;

    try {
      await deleteThoughtById(session.user.id, thoughtId);
      setThoughts(prevThoughts => prevThoughts.filter(thought => thought.id !== thoughtId));
    } catch (error) {
      console.error("Error deleting thought:", error);
    }
  };

  return (
    <Router>
      <div style={{
        ...styles.container,
        padding: 0,
        backgroundColor: colors.background,
      }}>
        {/* Add dark mode styles to the document */}
        <style>
          {`
            body {
              margin: 0;
              padding: 0;
              background-color: ${colors.background};
              color: ${colors.textPrimary};
            }

            /* Hide scrollbars globally */
            * {
              scrollbar-width: none; /* Firefox */
              -ms-overflow-style: none; /* IE and Edge */
            }

            *::-webkit-scrollbar {
              display: none; /* Chrome, Safari, Opera*/
            }

            /* Ensure inputs and textareas have dark mode */
            input, textarea {
              color: ${colors.textPrimary} !important;
              border-color: ${colors.divider} !important;
            }

            input::placeholder, textarea::placeholder {
              color: ${colors.textTertiary} !important;
            }
          `}
        </style>

        <ReviewModeProvider>

        {/* Header with login/logout (auto-hides while in drafts review) */}
        <AppHeader user={user} sidebarCollapsed={sidebarCollapsed} />

        {user && (
          <Routes>
            {/* Timeline / Shuffle / StarField need the FULL corpus, not the
                drafts-only `thoughts` array — they each fetch all thoughts via
                useAllThoughts(), lazily on mount. */}
            <Route
              path="/timeline"
              element={<TimelineView />}
            />
            <Route
              path="/shuffle"
              element={<ShuffleView />}
            />
            <Route
              path="/stars"
              element={<StarField />}
            />
            <Route
              path="/reflect"
              element={<ReflectView />}
            />
            <Route
              path="/deck"
              element={<DeckView />}
            />
            {/* Fuzzy keyword search over the full corpus (useAllThoughts). */}
            <Route
              path="/search"
              element={<SearchView />}
            />
            <Route
              path="/*"
              element={
                <AppContent
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  thoughts={thoughts}
                  saveThought={saveThought}
                  deleteThought={deleteThought}
                  themes={themes}
                  motifs={motifs}
                  tags={tags}
                  isDataLoading={isDataLoading}
                  updateThoughtLocally={updateThoughtLocally}
                  sidebarCollapsed={sidebarCollapsed}
                  setSidebarCollapsed={setSidebarCollapsed}
                />
              }
            />
          </Routes>
        )}
        </ReviewModeProvider>
      </div>
    </Router>
  );
}

export default App;
